import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderSlot as _renderSlot, createCommentVNode as _createCommentVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, Fragment as _Fragment, toDisplayString as _toDisplayString, vModelText as _vModelText, withKeys as _withKeys, withDirectives as _withDirectives, renderList as _renderList, withModifiers as _withModifiers, createVNode as _createVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, mergeProps as _mergeProps, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex-1" }
const _hoisted_2 = {
  key: 1,
  class: "block"
}
const _hoisted_3 = {
  key: 0,
  class: "block text-black"
}
const _hoisted_4 = ["placeholder", "onKeydown"]
const _hoisted_5 = {
  key: 1,
  class: "flex gap-2 flex-wrap"
}
const _hoisted_6 = ["placeholder", "onKeydown"]
const _hoisted_7 = {
  key: 2,
  class: "block text-black"
}
const _hoisted_8 = { class: "nk-select-arrow flex items-center" }
const _hoisted_9 = { class: "bg-white border border rounded-lg border-primary max-h-[18.75rem] overflow-x-hidden overflow-y-auto" }
const _hoisted_10 = {
  key: 1,
  class: "block text-center py-2 w-full text-gray text-xxs"
}

import {
  computed,
  provide,
  ref,
  useSlots,
  ComputedRef,
  Ref,
  Slots, onMounted, onBeforeUnmount, watch, VNode, nextTick,
} from 'vue';

import { get } from 'lodash';
import { NkIcon, NkPopper } from '@/core/components';
import { UPDATE_MODEL_EVENT } from '@/core/consts';

import { onClickOutside } from '@vueuse/core'

import { props as propsDefinition } from './select.shared';
import { StSelect } from './select.styled';

import {
  SelectOption,
  SelectProvide,
  ToggleActiveOption,
  SetHighlightedOptionIndex,
} from './select.types';


export default /*#__PURE__*/_defineComponent({
  __name: 'NkSelect',
  props: propsDefinition,
  emits: [UPDATE_MODEL_EVENT, 'change'],
  setup(__props, { emit }) {

const props = __props



const slots: Slots = useSlots();

const nkSelect: Ref<typeof StSelect | null> = ref(null);

const selectPopper: Ref<typeof NkPopper | null> = ref(null);

const open: Ref<boolean> = ref(false);
const isOpen = computed(() => open.value);

const searchInput: Ref<HTMLInputElement | null> = ref(null);
const searchValue: Ref<string> = ref('');

const getModelValue: ComputedRef<(number | string | boolean | object | null)[]> = computed(() => Array.isArray(props.modelValue) ? props.modelValue : [props.modelValue]);

const getOptions: ComputedRef<SelectOption[]> = computed(() => {
  const defaultSlots: VNode[] = slots.default ? slots.default() : [];
  let results: SelectOption[] = [];

  results = defaultSlots
    .filter((slot) => typeof slot.type === 'symbol' ? (slot.type as symbol).description !== 'Comment' : true)
    .map((slot) => typeof slot.type === 'symbol' ? slot.children : slot)
    .flat()
    .map((slot, index): SelectOption => ({
      _id: slot ? `${ JSON.stringify(slot.props.label).toLowerCase() }_${ JSON.stringify(slot.props.value) }` : '',
      label: slot ? slot.props.label : '',
      value: slot ? slot.props.value : '',
      index,
    }))
  ;

  return results;
});

const getFilteredOptions: ComputedRef<SelectOption[]> = computed(() => getOptions.value.filter(o => o.label.toString().toLowerCase().includes(searchValue.value.toLowerCase())));

const getActiveOptions: ComputedRef<SelectOption[]> = computed(() => getOptions.value
  .filter(o => getModelValue.value.find(v => get(v, props.valueKey, v) === get(o.value, props.valueKey, o.value))),
);

const highlightedOptionIndex: Ref<number | null> = ref(null);
const getHighlightedOption: ComputedRef<SelectOption | undefined> = computed(() => getFilteredOptions.value.find((o, index) => index === highlightedOptionIndex.value));

const getSearchInputContent = computed(() => {
  if (!getActiveOptions.value.length) {
    return 'Select';
  }

  return !props.multiple ? getActiveOptions.value[0].label : '';
});

const getRefreshKey = computed(() => getActiveOptions.value.map(o => o._id).join('-'));
const hasViableInsideSlots = computed(() => {
  const slot = slots.inside ? slots.inside()?.[0] : null;

  if (!slot) {
    return false;
  }

  return (slot.type as symbol).description !== 'Comment';
});

const refreshPopper = () => {
  open.value = false;
  setTimeout(() => {
    open.value = true;
  }, 1)
}

const addActiveOption = (option: SelectOption | undefined) => {
  if (!option) {
    return;
  }

  const modelValue = getModelValue.value;
  const optionValue = option.value;


  if (props.multiple) {
    const newModelValue = [...modelValue, optionValue];
    refreshPopper();

    emit(UPDATE_MODEL_EVENT, newModelValue);
    emit('change', newModelValue);
  } else {
    emit(UPDATE_MODEL_EVENT, optionValue);
    emit('change', optionValue);

    open.value = false;
  }
};

const removeActiveOption = (option: SelectOption) => {
  const modelValue = getModelValue.value;
  const optionValue = get(option.value, props.valueKey, option.value);

  const newModelValue = modelValue?.filter(o => get(o, props.valueKey, o) !== optionValue);

  refreshPopper();

  emit(UPDATE_MODEL_EVENT, newModelValue);
  emit('change', newModelValue);
};

const toggleActiveOption: ToggleActiveOption = (option) => {
  if (!option) {
    return;
  }

  if (props.multiple && getActiveOptions.value.includes(option)) {
    removeActiveOption(option);
  } else {
    addActiveOption(option);
  }
};

const removeLastOption = () => {
  if (!getActiveOptions.value.length || searchValue.value.length) {
    return;
  }

  removeActiveOption(getActiveOptions.value[getActiveOptions.value.length - 1]);
};

const setHighlightedOptionIndex: SetHighlightedOptionIndex = (index) => {
  highlightedOptionIndex.value = index;
};

const increaseHighlightedOptionIndex = () => {
  if (highlightedOptionIndex.value === null || highlightedOptionIndex.value >= (getFilteredOptions.value.length - 1)) {
    highlightedOptionIndex.value = 0;
  } else {
    highlightedOptionIndex.value += 1;
  }
};

const decreaseHighlightedOptionIndex = () => {
  if (highlightedOptionIndex.value === null) {
    highlightedOptionIndex.value = 0;
  } else if (highlightedOptionIndex.value <= 0) {
    highlightedOptionIndex.value = getFilteredOptions.value.length - 1;
  } else {
    highlightedOptionIndex.value -= 1;
  }
};

const onOpen = () => {
  if (props.filterable) {
    searchValue.value = '';
    searchInput.value?.focus();
  }

  highlightedOptionIndex.value = null;
};

const handlePopperClick = ({ target }: MouseEvent) => {
  if ((target as HTMLElement).closest('.nk-select')) {
    if (!props.filterable && open.value) {
      open.value = false;
      return;
    }

    if (props.filterable && open.value) {
      onOpen();
    }

    if (!open.value) {
      open.value = true;
    }
  }
};


const handlePopperClickAway = () => {
  if (isOpen.value) {
    open.value = false;
  }
};

onClickOutside(selectPopper as unknown as HTMLElement, handlePopperClickAway)

const stopPropagation = (e: KeyboardEvent, callback: () => void) => {
  e.preventDefault();
  e.stopPropagation();

  if (callback) {
    callback();
  }
};

const keyDownEventCallback = (e: KeyboardEvent) => {
  if (isOpen.value) {
    if ((e.key === 'Tab' || e.code === 'Tab')) {
      stopPropagation(e, increaseHighlightedOptionIndex);
    } else if ((e.key === 'Enter' || e.code === 'Enter') && highlightedOptionIndex.value !== null) {
      stopPropagation(e, toggleActiveOption.bind(undefined, getHighlightedOption.value));
    } else if (e.key === 'ArrowDown') {
      stopPropagation(e, increaseHighlightedOptionIndex);
    } else if (e.key === 'ArrowUp') {
      stopPropagation(e, decreaseHighlightedOptionIndex);
    }
  }
};

onMounted(() => {
  document.addEventListener('keydown', keyDownEventCallback);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', keyDownEventCallback);
});

watch(() => getFilteredOptions.value, () => {
  highlightedOptionIndex.value = null;
});

provide('select', {
  options: getOptions,
  activeOptions: getActiveOptions,
  filteredOptions: getFilteredOptions,
  highlightedOptionIndex,
  searchValue,
  toggleActiveOption,
  setHighlightedOptionIndex,
} as SelectProvide);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(NkPopper), {
    ref_key: "selectPopper",
    ref: selectPopper,
    inline: "",
    interactive: "",
    offsetDistance: "8",
    show: _unref(isOpen),
    "refresh-key": _unref(getRefreshKey),
    "onOpen:popper": onOpen,
    onClick: handlePopperClick
  }, {
    reference: _withCtx(() => [
      _createVNode(_unref(StSelect), _mergeProps(_ctx.$props, {
        ref_key: "nkSelect",
        ref: nkSelect,
        class: ["nk-select", {
          'is-open': _unref(isOpen),
        }]
      }), {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            (_ctx.$slots.inside)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  (_unref(hasViableInsideSlots))
                    ? _renderSlot(_ctx.$slots, "inside", { key: 0 })
                    : (_openBlock(), _createElementBlock("span", _hoisted_2, " Select "))
                ], 64))
              : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  (!props.multiple)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        (!props.filterable)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(_unref(getSearchInputContent)), 1))
                          : _withDirectives((_openBlock(), _createElementBlock("input", {
                              key: 1,
                              ref_key: "searchInput",
                              ref: searchInput,
                              class: "nk-select__search-input",
                              type: "text",
                              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchValue).value = $event)),
                              placeholder: _unref(getSearchInputContent),
                              onKeydown: _withKeys(removeLastOption, ["delete"])
                            }, null, 40, _hoisted_4)), [
                              [_vModelText, searchValue.value]
                            ])
                      ], 64))
                    : (props.multiple)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                          (_unref(getActiveOptions).length)
                            ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(_unref(getActiveOptions), (option) => {
                                return (_openBlock(), _createElementBlock("div", {
                                  class: "py-1 px-2 bg-gray-100 text-gray rounded-md text-xxs2 flex items-center gap-1",
                                  key: option._id
                                }, [
                                  _createTextVNode(_toDisplayString(option.label) + " ", 1),
                                  _createVNode(_unref(NkIcon), {
                                    onClick: _withModifiers(($event: any) => (removeActiveOption(option)), ["prevent","stop"]),
                                    class: "hover:text-primary",
                                    name: "NkClose",
                                    size: 3
                                  }, null, 8, ["onClick"])
                                ]))
                              }), 128))
                            : _createCommentVNode("", true),
                          (props.filterable)
                            ? _withDirectives((_openBlock(), _createElementBlock("input", {
                                key: 1,
                                ref_key: "searchInput",
                                ref: searchInput,
                                class: "nk-select__search-input",
                                type: "text",
                                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((searchValue).value = $event)),
                                placeholder: _unref(getSearchInputContent),
                                onKeydown: _withKeys(removeLastOption, ["delete"])
                              }, null, 40, _hoisted_6)), [
                                [_vModelText, searchValue.value]
                              ])
                            : (!props.filterable && !_unref(getActiveOptions).length)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_7, _toDisplayString(_unref(getSearchInputContent)), 1))
                              : _createCommentVNode("", true)
                        ]))
                      : _createCommentVNode("", true)
                ], 64))
          ]),
          _createElementVNode("div", _hoisted_8, [
            _createVNode(_unref(NkIcon), {
              name: "NkArrowBottom",
              size: 3
            })
          ])
        ]),
        _: 3
      }, 16, ["class"])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_9, [
        (_unref(getFilteredOptions).length)
          ? _renderSlot(_ctx.$slots, "default", { key: 0 })
          : (_openBlock(), _createElementBlock("span", _hoisted_10, " No matching data "))
      ])
    ]),
    _: 3
  }, 8, ["show", "refresh-key"]))
}
}

})